import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, SearchIcon, MailIcon, PhoneIcon } from 'lucide-react';
import { Customer } from '../types/Customer';
export function Customers() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const customers: Customer[] = [{
    id: '1',
    name: 'Acme Corporation',
    email: 'billing@acme.com',
    phone: '+66 12 345 6789',
    address: '123 Business Street',
    city: 'Bangkok',
    country: 'Thailand',
    createdAt: '2023-06-15',
    totalSpent: 875000,
    invoiceCount: 12,
    status: 'active'
  }, {
    id: '2',
    name: 'Tech Solutions Ltd',
    email: 'accounts@techsolutions.th',
    phone: '+66 11 234 5678',
    address: '456 Tech Park',
    city: 'Chiang Mai',
    country: 'Thailand',
    createdAt: '2023-08-20',
    totalSpent: 450000,
    invoiceCount: 8,
    status: 'active'
  }, {
    id: '3',
    name: 'Global Enterprises',
    email: 'finance@global.com',
    phone: '+66 13 456 7890',
    address: '789 Corporate Avenue',
    city: 'Phuket',
    country: 'Thailand',
    createdAt: '2023-04-10',
    totalSpent: 1250000,
    invoiceCount: 15,
    status: 'active'
  }, {
    id: '4',
    name: 'Innovation Hub',
    email: 'billing@innovationhub.th',
    phone: '+66 10 567 8901',
    address: '321 Innovation Drive',
    city: 'Bangkok',
    country: 'Thailand',
    createdAt: '2023-09-05',
    totalSpent: 180000,
    invoiceCount: 4,
    status: 'inactive'
  }];
  const getStatusBadge = (status: Customer['status']) => {
    const styles = {
      active: 'bg-green-50 text-green-700 border border-green-100',
      inactive: 'bg-gray-100 text-gray-600 border border-gray-200'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${styles[status]}`} data-id="element-7574">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>;
  };
  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = customer.name.toLowerCase().includes(searchTerm.toLowerCase()) || customer.email.toLowerCase().includes(searchTerm.toLowerCase()) || customer.phone && customer.phone.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || customer.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  const totalCustomers = filteredCustomers.length;
  const activeCustomers = filteredCustomers.filter(c => c.status === 'active').length;
  const totalRevenue = filteredCustomers.reduce((sum, c) => sum + c.totalSpent, 0);
  const avgRevenue = totalCustomers > 0 ? totalRevenue / totalCustomers : 0;
  return <div className="space-y-6 max-w-7xl mx-auto" data-id="element-7575">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-7576">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-7577">Customers</h1>
        <button onClick={() => navigate('/customer-form')} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-7578">
          Add customer
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-7579">
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-7580">
          <div className="text-sm font-medium text-gray-500" data-id="element-7581">
            Total Customers
          </div>
          <div className="mt-2 text-2xl font-semibold text-gray-900" data-id="element-7582">
            {totalCustomers}
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-7583">
          <div className="text-sm font-medium text-gray-500" data-id="element-7584">
            Active Customers
          </div>
          <div className="mt-2 text-2xl font-semibold text-green-600" data-id="element-7585">
            {activeCustomers}
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-7586">
          <div className="text-sm font-medium text-gray-500" data-id="element-7587">Total Revenue</div>
          <div className="mt-2 text-2xl font-semibold text-gray-900" data-id="element-7588">
            THB {totalRevenue.toLocaleString('en-US')}
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-7589">
          <div className="text-sm font-medium text-gray-500" data-id="element-7590">
            Avg. Revenue/Customer
          </div>
          <div className="mt-2 text-2xl font-semibold text-gray-900" data-id="element-7591">
            THB {Math.round(avgRevenue).toLocaleString('en-US')}
          </div>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-7592">
        {/* Controls Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border-b border-gray-200 gap-4" data-id="element-7593">
          {/* Status Filter Tabs */}
          <div className="flex space-x-6" data-id="element-7594">
            <button onClick={() => setStatusFilter('all')} className={`pb-4 -mb-4 text-sm font-medium transition-colors border-b-2 ${statusFilter === 'all' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-7595">
              All
            </button>
            <button onClick={() => setStatusFilter('active')} className={`pb-4 -mb-4 text-sm font-medium transition-colors border-b-2 ${statusFilter === 'active' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-7596">
              Active
            </button>
            <button onClick={() => setStatusFilter('inactive')} className={`pb-4 -mb-4 text-sm font-medium transition-colors border-b-2 ${statusFilter === 'inactive' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-7597">
              Inactive
            </button>
          </div>

          {/* Search */}
          <div className="relative w-64" data-id="element-7598">
            <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-7599" />
            <input type="text" placeholder="Search by customer" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors" data-id="element-7600" />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto" data-id="element-7601">
          <table className="w-full text-left border-collapse" data-id="element-7602">
            <thead data-id="element-7603">
              <tr className="border-b border-gray-100" data-id="element-7604">
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7605">
                  Customer
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7606">
                  Contact
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7607">
                  Location
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7608">
                  Total Spent
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7609">
                  Invoices
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-7610">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-7611">
              {filteredCustomers.map(customer => <tr key={customer.id} onClick={() => navigate(`/customer-detail/${customer.id}`)} className="hover:bg-gray-50 cursor-pointer transition-colors group" data-id="element-7612">
                  <td className="px-6 py-4" data-id="element-7613">
                    <div className="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors" data-id="element-7614">
                      {customer.name}
                    </div>
                    <div className="text-sm text-gray-500 mt-0.5" data-id="element-7615">
                      Since{' '}
                      {new Date(customer.createdAt).toLocaleDateString('en-GB')}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-7616">
                    <div className="text-sm text-gray-900" data-id="element-7617">
                      <div className="flex items-center gap-1.5 mb-1" data-id="element-7618">
                        <MailIcon size={13} className="text-gray-400" data-id="element-7619" />
                        {customer.email}
                      </div>
                      {customer.phone && <div className="flex items-center gap-1.5" data-id="element-7620">
                          <PhoneIcon size={13} className="text-gray-400" data-id="element-7621" />
                          {customer.phone}
                        </div>}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-7622">
                    <div className="text-sm text-gray-900" data-id="element-7623">{customer.city}</div>
                    <div className="text-sm text-gray-500 mt-0.5" data-id="element-7624">
                      {customer.country}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-7625">
                    <div className="text-sm font-medium text-gray-900" data-id="element-7626">
                      THB {customer.totalSpent.toLocaleString('en-US')}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-7627">
                    <div className="text-sm text-gray-900" data-id="element-7628">
                      {customer.invoiceCount}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-7629">
                    {getStatusBadge(customer.status)}
                  </td>
                </tr>)}
              {filteredCustomers.length === 0 && <tr data-id="element-7630">
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500 text-sm" data-id="element-7631">
                    No customers found matching your criteria.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}